<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :columns="visibleColumns"
            :items="tableData"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="true"
            rowClass="text-center"
            :sortBy="sortColumn"
            :sortOrder="sortDirection"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="search" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <!-- Status Filter -->
                    <FilterByType
                        :label="$t('billetNonDate.all_status')"
                        :options="statusOptions"
                        @filter-selected="handleStatusFilterChange"
                        :selected="statusFilter"
                    />

                    <!-- Export Button -->
                    <ExportSelect
                        :label="$t('billetNonDate.export')"
                        :options="[
                            { label: $t('billetNonDate.csv'), value: 'csv' },
                            { label: $t('billetNonDate.excel'), value: 'excel' },
                        ]"
                        :disabled="exportLoading"
                        :loading="exportLoading"
                        @export-selected="downloadBilletNonDate"
                    />

                    <div class="ppv2-flex-1"></div>
                    <!-- Column Visibility Toggle -->
                    <div class="ppv2-relative" ref="columnsDropdown">
                        <button
                            @click="showColumns = !showColumns"
                            class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        >
                            <FilePen class="ppv2-w-5 ppv2-h-5" />
                            <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('customers.columns') }}
                            </span>
                            <ChevronDown class="ppv2-w-4 ppv2-h-4" />
                        </button>
                        <!-- Dropdown -->
                        <div
                            v-if="showColumns"
                            class="menuDropdown ppv2-absolute ppv2-top-full ppv2-right-0 ppv2-mt-2 ppv2-rounded-md ppv2-overflow-hidden ppv2-z-50"
                        >
                            <div class="ppv2-max-h-[300px] ppv2-overflow-y-auto">
                                <label
                                    v-for="(column, index) in allColumns"
                                    :key="index"
                                    class="dropdownItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-1 ppv2-cursor-pointer ppv2-transition-colors"
                                >
                                    <BaseCheckbox
                                        :value="selectedColumnKeys.includes(column.key)"
                                        @input="toggleColumn(column.key)"
                                        size="sm"
                                        :hasBorderChecked="true"
                                    />
                                    <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(14) }">{{
                                        column.label
                                    }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Column: Statut (status) -->
            <template #column-status="{ item }">
                <div
                    class="ppv2-inline-flex ppv2-min-h-[28px] ppv2-items-center ppv2-justify-center ppv2-gap-2 ppv2-px-3 ppv2-rounded-xl ppv2-transition-all"
                    :class="getStatusBadgeClass(item.status)"
                >
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ $t(`billetNonDate.status_${item.status}`) }}
                    </span>
                </div>
            </template>

            <!-- Column: Code (code) -->
            <template #column-code="{ item }">
                <span class="ppv2-text-black poppinsRegular ppv2-underline" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.code }}
                </span>
            </template>

            <!-- Column: Offert par (gifted_by) -->
            <template #column-gifted_by="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.gifted_by }}
                </div>
            </template>

            <!-- Column: Offert à (gifted_to) -->
            <template #column-gifted_to="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.gifted_to }}
                </div>
            </template>

            <!-- Column: Disponibilité (send_date) -->
            <template #column-send_date="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.send_date }}
                </div>
            </template>

            <template #column-used_at="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.used_at }}
                </div>
            </template>

            <!-- Column: Date d'expiration (date_end) -->
            <template #column-date_end="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.date_end }}
                </div>
            </template>

            <!-- Column: Montant total -->
            <template #column-total_amount="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.total_amount }}{{ $symbolCurrency }}
                </span>
            </template>

            <!-- Column: Solde -->
            <template #column-available_amount="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.available_amount }}{{ $symbolCurrency }}
                </span>
            </template>

            <!-- Column: Client (client) -->
            <template #column-client="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.client || '-' }}
                </div>
            </template>

            <!-- Column: Contenu (items) -->
            <template #column-items="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    <template v-if="!item.items || item.items.length === 0"> {{ $t('billetNonDate.no_items') }} </template>
                    <template v-else>
                        <span v-for="(subItem, index) in item.items" :key="index">
                            <template v-if="subItem.item_id == -1"> {{ subItem.amount }}{{ $symbolCurrency }} </template>
                            <template v-else>
                                {{ subItem.quantity || 1 }} x
                                {{ subItem.voucher_itemable?.name || $t('billetNonDate.unnamed_item') }}
                            </template>
                            <span v-if="index < item.items.length - 1">, </span>
                        </span>
                    </template>
                </div>
            </template>

            <!-- Column: Note d'import (import_note) -->
            <template #column-import_note="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.import_note || '-' }}
                </div>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <button
                            @click="openEditModal(item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <SquarePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('common.edit')
                            }}</span>
                        </button>

                        <!-- Générer facture -->
                        <a
                            v-if="item.order_item && item.order_item.order"
                            :href="`/back-office/invoice/${item.order_item.order.id}`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <FileText :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ $t('billetNonDate.generate_invoice') }}
                            </span>
                        </a>
                        <!-- Historique -->
                        <a
                            :href="`/back-office/bons_cadeau/${item.id}/history?tab=billetsNonDatees`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <History :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('billetNonDate.history')
                            }}</span>
                        </a>
                        <!-- Renvoyer par email -->
                        <a
                            :href="`/back-office/bons_cadeau/${item.id}/send_billetNonDate`"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <Mail :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('billetNonDate.resend')
                            }}</span>
                        </a>
                        <!-- Télécharger PDF -->
                        <button
                            @click="downloadSinglePDF(item)"
                            :disabled="downloadingItemId === item.id"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <template v-if="downloadingItemId === item.id">
                                <svg
                                    class="ppv2-animate-spin ppv2-h-4 ppv2-w-4 ppv2-text-current"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        class="ppv2-opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    ></circle>
                                    <path
                                        class="ppv2-opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                            </template>
                            <Download v-else :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('billetNonDate.download')
                            }}</span>
                        </button>
                        <!-- Activer / Désactiver -->
                        <button
                            v-if="item.is_blocked"
                            @click="confirmToggleStatus(item, 'enable')"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <CheckCircle :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('billetNonDate.enable')
                            }}</span>
                        </button>
                        <button
                            v-else
                            @click="confirmToggleStatus(item, 'disable')"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <XCircle :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('billetNonDate.disable')
                            }}</span>
                        </button>
                    </div>
                </div>
            </template>
            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular textGray">
                    {{ $t('billetNonDate.selectedItems', { count: selectedCount, plural: selectedCount > 1 ? 's' : '' }) }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="bulkDownload"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                    :loading="bulkLoading"
                >
                    <Download :size="14" />
                    <span>{{ $t('billetNonDate.downloadSelection') }}</span>
                </BaseButton>
                <BaseButton
                    variant="outline"
                    @click="cancelSelection"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <XCircle :size="14" />
                    <span>{{ $t('billetNonDate.cancelSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Status Toggle Confirmation Dialog -->
        <CustomDialog
            v-model="showStatusPopup"
            :defaultTitle="statusAction ? $t(`billetNonDate.${statusAction}PopupTitle`) : ''"
            @cancel="showStatusPopup = false"
            @confirm="toggleStatus"
        >
            <template #content>
                <div class="ppv2-p-4">
                    {{ statusAction ? $t(`billetNonDate.${statusAction}PopupDescription`) : '' }}
                </div>
            </template>
        </CustomDialog>
        <CustomerVoucherEditModal
            v-if="showEditModal"
            v-model="showEditModal"
            :item="selectedBilletNonDate"
            :token="token"
            type="billetNonDate"
            @updated="handleBilletNonDateUpdated"
        />
    </div>
</template>

<script>
import {
    SquarePen,
    Trash2,
    Plus,
    MoreVertical,
    History,
    LayoutGrid,
    ChevronDown,
    Mail,
    Download,
    CheckCircle,
    XCircle,
    FileText,
    FilePen,
} from 'lucide-vue'
import { eventBus } from '../../../Customer/eventBus'
import DataTable from '../../../Shared/DataTable.vue'
import Search from '../../Experience/components/Search.vue'
import BaseCheckbox from '../../../Shared/BaseCheckbox.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import ExportSelect from '../../../Shared/ExportSelect.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'
import CustomerVoucherEditModal from '../Shared/CustomerVoucherEditModal.vue'
import FilterByType from '../../../Shared/FilterByType.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    name: 'BilletNonDateTable',
    props: {
        token: {
            type: String,
            default: '',
        },
    },
    components: {
        SquarePen,
        Trash2,
        Plus,
        MoreVertical,
        History,
        LayoutGrid,
        ChevronDown,
        Mail,
        Download,
        CheckCircle,
        CheckCircle,
        XCircle,
        FileText,
        FilePen,
        DataTable,
        Search,
        BaseCheckbox,
        BaseButton,
        CustomDialog,
        ExportSelect,
        CustomerVoucherEditModal,
        FilterByType,
    },
    data() {
        return {
            search: '',
            statusFilter: '',
            tableData: [],
            itemsPerPage: getPerPage('billets_non_dates_items_per_page', 10),
            recordsTotal: 0,
            currentPage: 1,
            loading: false,
            exportLoading: false,
            showColumns: false,
            selectedItems: [],
            selectAll: false,
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
            sortColumn: 'id',
            sortDirection: 'desc',

            // --- Status Toggle ---
            showStatusPopup: false,
            itemToUpdate: null,
            statusAction: '',

            bulkLoading: false,
            downloadingItemId: null,

            showEditModal: false,
            selectedBilletNonDate: null,
            // --- Colonnes pour les bons cadeaux ---
            selectedColumnKeys: [
                'code',
                'status',
                'total_amount',
                'available_amount',
                'client',
                'items',
                'created_at',
                'send_date',
                'used_at',
                'date_end',
                'gifted_to',
                'actions',
            ],
        }
    },
    computed: {
        allColumns() {
            return [
                { key: 'code', label: this.$t('billetNonDate.code'), sortable: true, type: 'text' },
                {
                    key: 'status',
                    label: this.$t('billetNonDate.status'),
                    sortable: false,
                    headerClass: 'ppv2-text-center',
                    type: 'badge',
                },
                {
                    key: 'total_amount',
                    label: this.$t('billetNonDate.total_amount'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'available_amount',
                    label: this.$t('billetNonDate.available_amount'),
                    sortable: true,
                    type: 'number',
                },
                { key: 'client', label: this.$t('billetNonDate.client'), sortable: false, type: 'text' },
                { key: 'items', label: this.$t('billetNonDate.items'), sortable: false, type: 'text' },
                { key: 'import_note', label: this.$t('billetNonDate.import_note'), sortable: false, type: 'text' },
                { key: 'send_date', label: this.$t('billetNonDate.send_date'), sortable: true, type: 'date' },
                { key: 'created_at', label: this.$t('billetNonDate.created_at'), sortable: true, type: 'date' },
                { key: 'used_at', label: this.$t('billetNonDate.used_at'), sortable: true, type: 'date' },
                { key: 'date_end', label: this.$t('billetNonDate.date_end'), sortable: true, type: 'date' },
                { key: 'gifted_to', label: this.$t('billetNonDate.gifted_to'), sortable: false, type: 'text' },
                { key: 'gifted_by', label: this.$t('billetNonDate.gifted_by'), sortable: false, type: 'text' },
                { key: 'actions', label: this.$t('common.actions'), sortable: false, type: 'actions' },
            ]
        },
        statusOptions() {
            return [
                { label: this.$t('billetNonDate.all_status'), value: '' },
                { label: this.$t('billetNonDate.status_active'), value: 'active' },
                { label: this.$t('billetNonDate.status_used'), value: 'used' },
                { label: this.$t('billetNonDate.status_expired'), value: 'expired' },
                { label: this.$t('billetNonDate.status_blocked'), value: 'blocked' },
                { label: this.$t('billetNonDate.status_partially_used'), value: 'partially_used' },
            ]
        },
        totalPages() {
            return Math.ceil(this.recordsTotal / this.itemsPerPage)
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        getStatusBadgeClass(status) {
            switch (status) {
                case 'active':
                    return 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                case 'used':
                    return 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                case 'expired':
                    return 'ppv2-border-[#911414] ppv2-bg-[#FF9C9C] ppv2-bg-opacity-20'
                case 'blocked':
                    return 'ppv2-border-[#911414] ppv2-bg-[#FF9C9C] ppv2-bg-opacity-20'
                case 'partially_used':
                    return 'ppv2-border-[#DBA800] ppv2-bg-[#DBA800] ppv2-bg-opacity-20'
                default:
                    return 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
            }
        },
        getStatusColor(status) {
            switch (status) {
                case 'active':
                    return 'ppv2-bg-green-500'
                case 'used':
                    return 'ppv2-bg-blue-500'
                case 'expired':
                    return 'ppv2-bg-red-500'
                case 'blocked':
                    return 'ppv2-bg-gray-500'
                case 'partially_used':
                    return 'ppv2-bg-yellow-500'
                default:
                    return 'ppv2-bg-gray-400'
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('billets_non_dates_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        toggleColumn(key) {
            if (this.selectedColumnKeys.includes(key)) {
                this.selectedColumnKeys = this.selectedColumnKeys.filter((k) => k !== key)
            } else {
                this.selectedColumnKeys.push(key)
            }
            this.fetchData()
        },
        handleSearchUpdate(searchValue) {
            this.search = searchValue
            this.currentPage = 1
            this.fetchData()
        },
        handleStatusFilterChange(value) {
            this.statusFilter = value
            this.currentPage = 1
            this.fetchData()
        },
        handleSort(key) {
            if (this.sortColumn === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortColumn = key
                this.sortDirection = 'asc'
            }
            this.fetchData()
        },
        handleClickOutside(event) {
            if (this.$refs.columnsDropdown && !this.$refs.columnsDropdown.contains(event.target)) {
                this.showColumns = false
            }

            if (this.activeActionMenu) {
                const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
                const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]

                const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
                const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu

                const clickedButton = btnEl && (btnEl === event.target || btnEl.contains(event.target))
                const clickedMenu = menuEl && (menuEl === event.target || menuEl.contains(event.target))

                if (!clickedButton && !clickedMenu) {
                    this.activeActionMenu = null
                }
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },

        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`, // 220 is min-width of dropdown
            }
        },

        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },
        getParams() {
            const params = {
                search: this.search,
                status: this.statusFilter,
                currentPage: this.currentPage,
                itemsPerPage: this.itemsPerPage,
                sortBy: this.sortColumn,
                sortDirection: this.sortDirection,
            }
            return params
        },
        async fetchData() {
            this.loading = true
            try {
                console.log(this.token)
                const params = this.getParams()
                const response = await axios.get('/api/v3/billetNonDate/list', {
                    params,
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                const data = response.data

                this.recordsTotal = data.total
                this.tableData = data.data || []
            } catch (error) {
                if (error.response?.status === 403) {
                    this.$toast.error(this.$t('errors_permission.noPermission'))
                } else {
                    this.$toast.error(error.response?.data?.message || error.message)
                }
            } finally {
                this.loading = false
            }
        },

        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.tableData.length
        },
        toggleSelectAll() {
            this.selectAll = !this.selectAll
            if (this.selectAll) {
                this.selectedItems = this.tableData.map((item) => item.id)
            } else {
                this.selectedItems = []
            }
        },
        async downloadBilletNonDate(format = 'csv') {
            this.exportLoading = true
            try {
                const params = this.getParams()
                params.format = format
                const response = await axios.get('/api/v3/billetNonDate/export', {
                    params,
                    headers: { Authorization: `Bearer ${this.token}` },
                    responseType: 'blob',
                })

                const blob = new Blob([response.data])
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `billetNonDate-${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
                this.$toast?.success?.(this.$t('billetNonDate.exportSuccess'))
            } catch (error) {
                if (error.response?.status === 403) {
                    this.$toast.error(this.$t('errors_permission.noPermission'))
                } else {
                    this.$toast.error(error.response?.data?.message || error.message)
                }
            } finally {
                this.exportLoading = false
            }
        },

        async bulkDownload() {
            if (this.selectedItems.length === 0) return

            this.bulkLoading = true
            try {
                const response = await axios.post(
                    '/api/v3/billetNonDate/bulk-download',
                    { ids: this.selectedItems },
                    {
                        headers: { Authorization: `Bearer ${this.token}` },
                        responseType: 'blob',
                    }
                )

                // Check if the response is actually a ZIP file
                if (response.data.type === 'application/json') {
                    // If it's JSON, it's likely an error message returned as a blob
                    const reader = new FileReader()
                    reader.onload = () => {
                        const errorData = JSON.parse(reader.result)
                        this.$toast?.error?.(errorData.message || this.$t('billetNonDate.bulkDownloadError'))
                    }
                    reader.readAsText(response.data)
                    this.bulkLoading = false
                    return
                }

                const url = window.URL.createObjectURL(response.data)
                const link = document.createElement('a')
                link.href = url
                const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
                link.setAttribute('download', `billetNonDate-${timestamp}.zip`)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)

                this.$toast?.success?.(this.$t('billetNonDate.bulkDownloadSuccess'))
            } catch (error) {
                if (error.response?.status === 403) {
                    this.$toast.error(this.$t('errors_permission.noPermission'))
                } else {
                    this.$toast.error(error.response?.data?.message || error.message)
                }
            } finally {
                this.bulkLoading = false
            }
        },
        cancelSelection() {
            this.selectedItems = []
            this.selectAll = false
        },
        async downloadSinglePDF(item) {
            this.downloadingItemId = item.id
            try {
                const response = await axios.get(`/api/v3/billetNonDate/${item.id}/download`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                    responseType: 'blob',
                })
                const blob = new Blob([response.data], { type: 'application/pdf' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `billetNonDate-${item.code}.pdf`)
                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch (error) {
                if (error.response?.status === 403) {
                    this.$toast.error(this.$t('errors_permission.noPermission'))
                } else {
                    this.$toast.error(error.response?.data?.message || error.message)
                }
            } finally {
                this.downloadingItemId = null
            }
        },

        confirmToggleStatus(item, action) {
            this.itemToUpdate = item
            this.statusAction = action
            this.showStatusPopup = true
        },

        async toggleStatus() {
            if (!this.itemToUpdate) return

            try {
                await axios.post(
                    `/api/v3/billetNonDate/${this.itemToUpdate.id}/toggle-status`,
                    {
                        action: this.statusAction,
                    },
                    {
                        headers: { Authorization: `Bearer ${this.token}` },
                    }
                )
                this.$toast?.success?.(this.$t('billetNonDate.statusUpdateSuccess'))
                this.fetchData()
            } catch (error) {
                if (error.response?.status === 403) {
                    this.$toast.error(this.$t('errors_permission.noPermission'))
                } else {
                    this.$toast.error(error.response?.data?.message || error.message)
                }
            } finally {
                this.showStatusPopup = false
                this.itemToUpdate = null
            }
        },
        openEditModal(billetNonDate) {
            this.selectedBilletNonDate = billetNonDate
            this.showEditModal = true
            this.activeActionMenu = null
        },
        handleBilletNonDateUpdated(updatedBilletNonDate) {
            // Update the item in tableData if needed, or just refresh
            const index = this.tableData.findIndex((v) => v.id === updatedBilletNonDate.id)
            if (index !== -1) {
                // Merge updated properties or replace
                this.tableData.splice(index, 1, { ...this.tableData[index], ...updatedBilletNonDate })
            } else {
                this.fetchData()
            }
        },
    },
    mounted() {
        this.fetchData()
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('scroll', this.updateDropdownPosition, true)
        window.addEventListener('resize', this.updateDropdownPosition)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('scroll', this.updateDropdownPosition, true)
        window.removeEventListener('resize', this.updateDropdownPosition)
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.tableData.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },
        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },
    },
}
</script>

<style scoped lang="scss">
.menuDropdown {
    background: white;
    color: #94a3b8;
    margin-top: 10px;
    z-index: 50;
    padding: 8px 0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdownItem:hover {
    background-color: #1a1f37;
    color: white !important;
}

.dropdownItem:hover * {
    color: white !important;
}

.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.actionItem:hover * {
    color: #1a1f37 !important;
}

.dark {
    .menuDropdown {
        background-color: #2c2c2ce5;
    }

    .actionsDropdown {
        background-color: #2c2c2ce5;
    }

    .actionItem {
        color: #bfbfbf;
    }

    .actionItem:hover {
        background-color: #1a1f37;
        color: white !important;
    }

    .actionItem:hover * {
        color: white !important;
    }
}

.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}

.vue-dialog {
    font-size: clamp(12px, 0.729545vw, 16px);
    width: 100% !important;
    padding-bottom: 35px;
    text-align: center;
    max-width: 600px;
    left: 0 !important;
    margin: auto;
}

.vue-dialog-button b {
    line-height: 44px;
}

.vue-dialog-buttons {
    border-top: none !important;
    gap: 20px;
}

.vue-dialog-button:not(:first-of-type) {
    border-left: none !important;
}

.vue-dialog-content-title {
    min-height: 1.75rem;
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.75rem;
    border-bottom: 1px solid #eee;
}

.vue-dialog-content {
    padding: 14px 0;
}

@media (max-width: 767.98px) {
    .vue-dialog-button b {
        line-height: 30px;
    }
}
</style>
